import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getActivity, deleteActivity, updateActivity } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ActivitySummaryStats } from "../components/ActivitySummaryStats";
import { ZoneChart } from "../components/ZoneChart";
import { PowerCurve } from "../components/PowerCurve";
import { LapsView } from "../components/LapsView";
import { AnalyticsView } from "../components/AnalyticsView";
import { PeaksView } from "../components/PeaksView";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDateTime } from "../lib/format";
import { formatActivityDateTime } from "../lib/dateTime";
import { resolveActivityPowerMetrics } from "../lib/powerMetrics";
import { getTemperatureStats } from "../lib/peaks";
import { computePowerZonesFromSamples, computeHrZonesFromSamples } from "../lib/zones";
import {
  activityAfterRename,
  activityHasSamples,
  isCyclingActivity,
  positiveMetric,
  showCyclingPowerCurve,
  showCyclingPowerZones,
} from "../lib/sportAnalyticsGate";
import { deriveActivityName } from "../lib/activityName";
import { ChevronLeft, Columns2, Trash2, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import { ActivityCoachButton } from "@/components/coaching/ActivityCoachButton";
import { ActivityCoachDrawer } from "@/components/coaching/ActivityCoachDrawer";
import { SportBadge } from "../components/SportBadge";

export const ActivityDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const unitSystem = user?.unit_system || "metric";
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getActivity(id)
      .then((d) => {
        if (!cancelled) {
          setActivity(d);
          setNameDraft(deriveActivityName(d));
        }
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const ridePowerMetrics = useMemo(
    () => resolveActivityPowerMetrics(activity, user?.ftp ?? null),
    [activity, user?.ftp]
  );

  const temperatureStats = useMemo(() => {
    if (!activity?.samples?.length) return null;
    return getTemperatureStats(activity.samples);
  }, [activity?.samples]);

  const powerZones = useMemo(() => {
    if (!activity || !showCyclingPowerZones(activity, user?.ftp)) return null;
    if (activity.power_zones) return activity.power_zones;
    return computePowerZonesFromSamples(activity.samples || [], user.ftp);
  }, [activity, user?.ftp]);

  const hrZones = useMemo(() => {
    if (!activity) return null;
    if (activity.hr_zones) return activity.hr_zones;
    if (activity.has_hr) {
      return computeHrZonesFromSamples(activity.samples || [], user?.max_hr || 190);
    }
    return null;
  }, [activity, user?.max_hr]);

  const handleDelete = async () => {
    if (!confirm("Delete this activity?")) return;
    setDeleting(true);
    try {
      await deleteActivity(id);
      toast.success("Activity deleted");
      nav("/");
    } catch (e) {
      toast.error("Delete failed");
      setDeleting(false);
    }
  };

  const handleSaveName = async () => {
    if (!nameDraft.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingName(true);
    try {
      const updated = await updateActivity(id, { name: nameDraft.trim() });
      setActivity((current) => activityAfterRename(current, updated));
      setNameDraft(updated.name || nameDraft.trim());
      setEditingName(false);
      toast.success("Name updated");
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-5 w-36 app-card animate-pulse" />
        <div className="h-28 app-card animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-24 metric-card animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="app-card p-10 text-center">
        <div className="text-analytics-red font-heading text-2xl uppercase tracking-wider">
          Activity Not Found
        </div>
        <p className="text-sm text-dark-text-secondary mt-2">{error}</p>
        <Link to="/" className="inline-block mt-4 text-xs tracking-widest uppercase text-brand-primary hover:underline">
          ← Back to Activities
        </Link>
      </div>
    );
  }

  const samples = activity.samples || [];
  const hasFileLaps = (activity.laps?.length ?? 0) > 0;
  const samplesReady = activityHasSamples(activity);
  const detailSamplesUnavailable = !samplesReady && Number(activity.sample_count || 0) > 0;
  const showPowerZones = samplesReady && showCyclingPowerZones(activity, user?.ftp) && powerZones;
  const showPowerCurve = showCyclingPowerCurve(activity);
  const showFtpPrompt =
    samplesReady &&
    isCyclingActivity(activity) &&
    activity.has_power &&
    !positiveMetric(user?.ftp);
  const showHrZones = samplesReady && activity.has_hr && hrZones;

  return (
    <div className="space-y-8">
      {/* Header */}
      <header className="space-y-5">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-dark-text-muted hover:text-dark-text-primary transition-colors"
          data-testid="back-to-activities-link"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          Back to Activities
        </Link>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="meta-text font-mono text-[11px] uppercase tracking-wide">
              {formatActivityDateTime(activity)}
              {activity.filename ? (
                <span className="text-dark-text-muted"> · {activity.filename}</span>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {editingName ? (
                <div className="flex flex-wrap items-center gap-2 w-full">
                  <input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="app-control font-mono text-sm w-full max-w-md"
                    data-testid="activity-name-input"
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={savingName}
                    className="btn-primary-app px-4 py-2 text-xs tracking-[0.2em] uppercase"
                    data-testid="activity-name-save"
                  >
                    <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingName(false);
                      setNameDraft(deriveActivityName(activity));
                    }}
                    className="btn-secondary-app px-4 py-2 text-xs tracking-[0.2em] uppercase"
                    data-testid="activity-name-cancel"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <h1
                    className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-dark-text-primary"
                    data-testid="activity-title"
                  >
                    {deriveActivityName(activity)}
                  </h1>
                  <SportBadge activity={activity} className="shrink-0" />
                </>
              )}
            </div>
          </div>

          {!editingName && (
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <ActivityCoachButton onClick={() => setCoachOpen(true)} />
              <Link
                to={`/activity/${id}/compare`}
                className="btn-secondary-app px-4 py-2.5 text-xs tracking-[0.2em] uppercase"
                data-testid="compare-activity-button"
              >
                <Columns2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                Compare
              </Link>
              <button
                onClick={() => setEditingName(true)}
                className="btn-secondary-app px-4 py-2.5 text-xs tracking-[0.2em] uppercase"
                data-testid="activity-name-edit"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn-danger-app px-4 py-2.5 text-xs tracking-[0.2em] uppercase"
                data-testid="delete-activity-button"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                Delete
              </button>
            </div>
          )}
        </div>
      </header>

      <ActivityCoachDrawer
        open={coachOpen}
        onOpenChange={setCoachOpen}
        activityId={activity.id}
        activitySport={activity.sport}
        activitySportSubtype={activity.sport_subtype}
      />

      {/* Summary stats */}
      <ActivitySummaryStats
        activity={activity}
        unitSystem={unitSystem}
        ridePowerMetrics={ridePowerMetrics}
        userFtp={user?.ftp || null}
        temperatureStats={temperatureStats}
      />

      {/* Overview always visible; Peaks + optional Laps as extra tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="app-card inline-flex h-auto w-full sm:w-auto gap-1 p-1 rounded-md">
          <TabsTrigger
            value="overview"
            className="flex-1 sm:flex-none rounded-md px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold text-dark-text-secondary transition-colors data-[state=active]:bg-brand-primary data-[state=active]:text-black data-[state=active]:shadow-sm"
            data-testid="tab-overview"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="peaks"
            className="flex-1 sm:flex-none rounded-md px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold text-dark-text-secondary transition-colors data-[state=active]:bg-brand-primary data-[state=active]:text-black data-[state=active]:shadow-sm"
            data-testid="tab-peaks"
          >
            Peaks
          </TabsTrigger>
          {hasFileLaps && (
            <TabsTrigger
              value="laps"
              className="flex-1 sm:flex-none rounded-md px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold text-dark-text-secondary transition-colors data-[state=active]:bg-brand-primary data-[state=active]:text-black data-[state=active]:shadow-sm"
              data-testid="tab-laps"
            >
              Laps
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-8">
          {detailSamplesUnavailable ? (
            <p className="text-sm text-dark-text-secondary" data-testid="sample-detail-unavailable">
              Detailed sample data is no longer stored for this activity, so charts, the map, and range selection cannot be shown. The summary numbers above are still from the original recording.
            </p>
          ) : !samplesReady ? (
            <p className="text-sm text-dark-text-secondary" data-testid="sample-detail-unavailable">
              This activity has summary numbers only. Power zones, the power curve, and peaks stay hidden until a recording is available.
            </p>
          ) : (
            <>
          <AnalyticsView
            activity={activity}
            samples={samples}
            userFtp={isCyclingActivity(activity) ? (user?.ftp || null) : null}
            unitSystem={unitSystem}
          />

          {(showHrZones || showPowerZones || showFtpPrompt || showPowerCurve) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {showHrZones && (
                <ZoneChart
                  zones={hrZones}
                  title="Heart Rate Zones"
                  type="hr"
                />
              )}
              {showPowerZones && (
                <ZoneChart
                  zones={powerZones}
                  title="Power Zones"
                  type="power"
                />
              )}
              {showFtpPrompt && (
                <div
                  className="app-card border-dashed p-6 flex flex-col justify-center"
                  data-testid="power-zones-empty"
                >
                  <div className="text-[10px] tracking-[0.3em] uppercase text-dark-text-muted">Distribution</div>
                  <h3 className="font-heading text-xl tracking-wider uppercase text-dark-text-primary mt-0.5 mb-3">
                    Power Zones
                  </h3>
                  <p className="text-xs text-dark-text-secondary leading-relaxed">
                    Power data detected. Set your{" "}
                    <a href="/settings" className="text-[#FF9F0A] hover:underline">FTP</a>{" "}
                    in Settings to see zone distribution.
                  </p>
                </div>
              )}

              {showPowerCurve && (
                <div className="lg:col-span-2">
                  <PowerCurve curve={activity.power_curve} ftp={user?.ftp || null} />
                </div>
              )}
            </div>
          )}
            </>
          )}
        </TabsContent>

        <TabsContent value="peaks" className="mt-6">
          {!samplesReady ? (
            <p className="text-sm text-dark-text-secondary" data-testid="peaks-samples-unavailable">
              Peak metrics need the original sample stream, which is not available for this activity.
            </p>
          ) : (
          <PeaksView
            activity={activity}
            samples={samples}
            userFtp={user?.ftp || null}
            userWeightKg={user?.weight_kg || null}
            userMaxHr={user?.max_hr || 190}
          />
          )}
        </TabsContent>

        {hasFileLaps && (
          <TabsContent value="laps" className="mt-6">
            <LapsView
              activity={activity}
              samples={samples}
              userFtp={user?.ftp || null}
              userMaxHr={user?.max_hr || 190}
              unitSystem={unitSystem}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
